import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldWrap, NativeSelect, ChoicePills } from "@/components/Field";
import { ReceiptField, ReceiptsField, stillUploading } from "@/components/Receipt";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

function SuggestInput({ id, value, onChange, placeholder, endpoint }) {
  const [names, setNames] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setNames(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <>
      <Input id={id} list={`${id}-list`} autoComplete="off" className="h-11" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={id} />
      <datalist id={`${id}-list`} data-testid={`${id}-suggestions`}>{names.map((n) => <option key={n} value={n} />)}</datalist>
    </>
  );
}

function FormField({ field, value, onChange, ctx, tid }) {
  const id = `${tid}-${field.key}`;
  switch (field.type) {
    case "church":
      return <NativeSelect value={value} onChange={onChange} placeholder="Choose church..." options={ctx.churches.map((c) => ({ value: c.id, label: c.name }))} testid={id} />;
    case "choice":
      return <ChoicePills value={value} onChange={onChange} options={field.options(ctx)} testid={id} />;
    case "select":
      return <NativeSelect value={value} onChange={onChange} placeholder={field.required ? "Choose..." : "None"} options={field.options(ctx)} testid={id} />;
    case "money":
      return <Input id={id} type="number" inputMode="decimal" min="0" step="0.01" className="h-11 text-lg" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} placeholder="0.00" />;
    case "count":
      return <Input id={id} type="number" inputMode="numeric" min="0" step="1" className="h-11 text-lg" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} placeholder="0" />;
    case "date":
      return <Input id={id} type="date" className="h-11" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
    case "textarea":
      return <Textarea id={id} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
    case "receipt":
      return <ReceiptField value={value} onChange={onChange} testid={id} />;
    case "receipts":
      return <ReceiptsField value={value} onChange={onChange} testid={id} />;
    case "suggest":
      return <SuggestInput id={id} value={value} onChange={onChange} placeholder={field.placeholder} endpoint={field.suggest} />;
    default:
      return <Input id={id} className="h-11" value={value ?? ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  }
}

function toPayload(fields, values) {
  const out = {};
  fields.forEach((f) => {
    const v = values[f.key];
    if (f.custom) out.custom = { ...(out.custom || {}), [f.key]: parseInt(v, 10) || 0 };
    else if (f.type === "money") out[f.key] = parseFloat(v) || 0;
    else if (f.type === "count") out[f.key] = parseInt(v, 10) || 0;
    else if (f.type === "receipt") out[f.key] = v || null;
    else if (f.type === "receipts") { out[f.key] = v || []; out.receipt_id = (v || [])[0] || null; }
    else out[f.key] = v ?? "";
  });
  return out;
}

export function RecordDialog({ cfg, preset, open, record, onClose, onSaved }) {
  const ctx = useApp();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const tid = cfg.endpoint;

  useEffect(() => {
    if (open) { setValues(record ? { ...record, ...(record.custom || {}), receipt_ids: record.receipt_ids?.length ? record.receipt_ids : record.receipt_id ? [record.receipt_id] : [] } : { ...cfg.defaults(ctx), ...(preset || {}) }); }
  }, [open, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = cfg.fields.filter((f) => (f.type !== "church" || ctx.isAdmin) && ((f.type !== "receipt" && f.type !== "receipts") || ctx.isAdmin || ctx.featureOn("receipts")) && (!f.when || f.when(ctx)));

  const set = (field, v) => {
    setValues((prev) => {
      const next = { ...prev, [field.key]: v };
      if (field.type === "church") {
        const church = ctx.churches.find((c) => c.id === v);
        cfg.fields.filter((f) => f.fromChurch).forEach((f) => { next[f.key] = church?.[f.fromChurch] || ""; });
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (stillUploading()) return;
    const missing = fields.find((f) => f.required && (values[f.key] === undefined || values[f.key] === "" || values[f.key] === null));
    if (missing) { toast.error(`Please fill in: ${missing.label}`); return; }
    setSaving(true);
    try {
      const payload = { ...toPayload(cfg.fields, values), ...(cfg.sideScoped && values.destination ? { destination: values.destination } : {}) };
      if (!ctx.isAdmin) delete payload.church_id;
      const res = record ? await api.put(`/${cfg.endpoint}/${record.id}`, payload) : await api.post(`/${cfg.endpoint}`, payload);
      toast.success(res?.data?.offline ? "Saved on this device — it will sync when you're back online" : record ? "Saved changes" : `${cfg.singular} saved`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const hint = cfg.hint?.(values, ctx);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid={`${tid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{record ? `Edit ${cfg.singular}` : `New ${cfg.singular}`}</DialogTitle>
          {!ctx.isAdmin && ctx.myChurch && <p className="text-sm text-slate-500">{ctx.myChurch.name}</p>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {fields.map((f) => (
            <FieldWrap key={f.key} label={f.label} sub={f.sub} htmlFor={`${tid}-${f.key}`} required={f.required}>
              <FormField field={preset?.[f.key] && f.options ? { ...f, options: () => [preset[f.key]] } : f} value={values[f.key]} onChange={(v) => set(f, v)} ctx={ctx} tid={tid} />
            </FieldWrap>
          ))}
          {hint && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`${tid}-hint`}>
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{hint.text} {!record && <Link to={hint.to} className="font-semibold underline" onClick={onClose}>{hint.cta}</Link>}</span>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid={`${tid}-cancel-btn`}>Cancel</Button>
            <Button type="submit" disabled={saving} className="min-w-32" data-testid={`${tid}-submit-btn`}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
